import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaGraduationCap, FaLaptopCode } from 'react-icons/fa';
import skillsData from '../data/skills.json';

const About = () => {
  const [skills, setSkills] = useState([]);

  useEffect(() => {
    // Load skills from the JSON file
    setSkills(skillsData);
  }, []);

  return (
    <AboutContainer>
      <AboutContent
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <AboutGrid>
          <ContentSection>
            <HeaderSection>
              <Title>About Me</Title>
              <Subtitle>Passionate IT Student & Web Developer</Subtitle>
            </HeaderSection>
            
            <Description>
              Hello! I'm Rishi Poddar, a dedicated student pursuing my BSc in Information Technology 
              at Thakur College of Science and Commerce. My passion lies in creating innovative web 
              solutions and exploring new technologies. I believe in continuous learning and applying 
              theoretical knowledge to practical projects.
            </Description>

            <EducationSection>
              <SectionTitle>
                <FaGraduationCap /> Education
              </SectionTitle>
              <EducationCard>
                <SchoolName>Thakur College of Science and Commerce</SchoolName>
                <DegreeName>BSc in Information Technology</DegreeName>
                <Timeline>2023 - Present</Timeline>
              </EducationCard>
            </EducationSection>

            <SkillsSection>
              <SectionTitle>
                <FaLaptopCode /> Technical Skills
              </SectionTitle>
              <SkillsGrid>
                {skills.map(skillGroup => (
                  <SkillCard 
                    key={skillGroup.id}
                    whileHover={{ y: -5 }} 
                    transition={{ duration: 0.2 }}
                  >
                    <SkillTitle>{skillGroup.title}</SkillTitle>
                    <SkillsList>
                      {skillGroup.items.map((item, index) => (
                        <SkillItem key={index}>{item}</SkillItem>
                      ))}
                    </SkillsList>
                  </SkillCard>
                ))}
              </SkillsGrid>
            </SkillsSection>
          </ContentSection>

          <ImageSection>
            <ProfileImageContainer>
              <ProfileImage 
                src="https://iili.io/3nvZZeS.png/400/500" 
                alt="Rishi Poddar"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
              />
              <ImageOverlay />
            </ProfileImageContainer>
          </ImageSection>
        </AboutGrid>
      </AboutContent>
    </AboutContainer>
  );
};

const AboutContainer = styled.div`
  min-height: 100vh;
  padding: 120px 20px 50px;
  background-color: var(--background);
`;

const AboutContent = styled(motion.div)`
  max-width: 1200px;
  margin: 0 auto;
`;

const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 50px;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const ContentSection = styled.div`
  padding-right: 20px;
`;

const HeaderSection = styled.div`
  margin-bottom: 40px;
`;

const Title = styled.h1`
  font-size: 3rem;
  color: var(--accent);
  margin-bottom: 10px;
`;

const Subtitle = styled.h2`
  font-size: 1.5rem;
  color: var(--text-secondary);
  font-weight: 400;
`;

const Description = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  color: var(--text-primary);
  margin-bottom: 40px;
`;

const SectionTitle = styled.h3`
  font-size: 1.5rem;
  color: var(--text-primary);
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    color: var(--accent);
  }
`;

const EducationSection = styled.div`
  margin-bottom: 40px;
`;

const EducationCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 20px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const SchoolName = styled.h4`
  font-size: 1.2rem;
  color: var(--accent);
  margin-bottom: 5px;
`;

const DegreeName = styled.p`
  font-size: 1rem;
  color: var(--text-primary);
  margin-bottom: 5px;
`;

const Timeline = styled.p`
  font-size: 0.9rem;
  color: var(--text-secondary);
`;

const SkillsSection = styled.div`
  margin-bottom: 40px;
`;

const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
`;

const SkillCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  padding: 25px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  
  &:hover {
    border-color: var(--accent);
    box-shadow: 0 10px 30px -15px rgba(2, 12, 27, 0.7);
  }
`;

const SkillTitle = styled.h4`
  font-size: 1.1rem;
  color: var(--accent);
  margin-bottom: 15px;
`;

const SkillsList = styled.ul`
  list-style: none;
  padding: 0;
`;

const SkillItem = styled.li`
  color: var(--text-primary);
  margin-bottom: 8px;
  font-size: 1rem;
  
  &:before {
    content: '▹';
    color: var(--accent);
    margin-right: 8px;
  }
`;

const ImageSection = styled.div`
  position: relative;
  
  @media (max-width: 968px) {
    width: 70%;
    margin: 0 auto;
  }
  
  @media (max-width: 480px) {
    width: 100%;
  }
`;

const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(100, 255, 218, 0.1);
  transition: all 0.3s ease;
`;

const ProfileImageContainer = styled.div`
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  
  &:hover {
    ${ImageOverlay} {
      opacity: 0;
    }
  }
`;

const ProfileImage = styled(motion.img)`
  width: 100%;
  height: auto;
  border-radius: 10px;
  filter: grayscale(100%);
  transition: all 0.3s ease;
  
  &:hover {
    filter: none;
  }
`;

export default About;